import { computed, inject, Injectable, signal } from '@angular/core';
import { CurrentDeviceService } from '../current-device/current-device.service';
import { FindLatestCashRegisterSessionResponseDto } from '../../cash-drawers/cash-register-session/dtos/find-latest-cash-register-session.response.dto';
import { AuthApiService } from '../../../core/auth/auth-api.service';
import { CashRegisterSessionsApiService } from '../../cash-drawers/cash-register-session/cash-register-session-api.service';
import { FindMyOpenCashRegisterSessionResponseDto } from '../../cash-drawers/cash-register-session/dtos/find-my-open-cash-register-session.response.dto';

@Injectable({
  providedIn: 'root',
})
export class CurrentRegisterSessionService {
  #auth = inject(AuthApiService);
  #currentDevice = inject(CurrentDeviceService);
  #registerSessionApi = inject(CashRegisterSessionsApiService);

  #loading = signal(false);
  #initializationPromise: Promise<void> | null = null;

  // Latest session for the register currently associated with this device.
  #currentRegisterSession =
    signal<FindLatestCashRegisterSessionResponseDto | null>(null);

  // Any open register session belonging to the currently authenticated user.
  #currentUserOpenRegisterSession =
    signal<FindMyOpenCashRegisterSessionResponseDto | null>(null);

  loading = this.#loading.asReadonly();

  currentRegisterSession = this.#currentRegisterSession.asReadonly();

  currentUserOpenRegisterSession =
    this.#currentUserOpenRegisterSession.asReadonly();

  exists = computed(() => !!this.#currentRegisterSession()?.id);

  isOpen = computed(
    () => this.exists() && this.#currentRegisterSession()?.closedAt == null,
  );

  isClosed = computed(() => !this.isOpen());

  user = computed(() => {
    const user = this.#currentRegisterSession()?.user;

    if (!user) {
      return undefined;
    }

    const { firstName, lastName } = user.contactInfo ?? {};

    return {
      ...user,
      name: firstName && lastName ? `${firstName} ${lastName}` : 'Unknown User',
    };
  });

  isOpenedByCurrentUser = computed(
    () => this.user()?.id === this.#auth.currentUserId(),
  );

  hasCurrentUserOpenRegisterSession = computed(
    () => !!this.#currentUserOpenRegisterSession()?.id,
  );

  canOpenRegister = computed(
    () => this.isClosed() && !this.hasCurrentUserOpenRegisterSession(),
  );

  canCloseRegister = computed(
    () =>
      this.isOpen() &&
      (this.isOpenedByCurrentUser() || this.#auth.isCurrentUserAdmin()),
  );

  async init(): Promise<void> {
    if (this.#initializationPromise) {
      await this.#initializationPromise;
      return;
    }

    if (this.currentRegisterSession() !== null) return;

    this.#initializationPromise = (async () => {
      await this.#currentDevice.init();
      await this.loadSession();
    })();
    try {
      await this.#initializationPromise;
    } finally {
      this.#initializationPromise = null;
    }
  }

  // Refresh the register session to ensure its totals are up to date.
  async refresh() {
    await this.loadSession();
  }

  async loadSession() {
    this.#loading.set(true);

    try {
      await Promise.all([
        this.loadCurrentRegisterSession(),
        this.loadCurrentUserOpenRegisterSession(),
      ]);
    } finally {
      this.#loading.set(false);
    }
  }

  reset(): void {
    this.#currentRegisterSession.set(null);
    this.#currentUserOpenRegisterSession.set(null);
  }

  private async loadCurrentRegisterSession() {
    const registerId = this.#currentDevice.registerId();

    if (!registerId) {
      this.#currentRegisterSession.set(null);
      return;
    }

    const session =
      await this.#registerSessionApi.findLatestCashRegisterSession(registerId);

    this.#currentRegisterSession.set(session);
  }

  private async loadCurrentUserOpenRegisterSession() {
    try {
      const session =
        await this.#registerSessionApi.findMyOpenCashRegisterSession();

      this.#currentUserOpenRegisterSession.set(session);
    } catch (error) {
      this.#currentUserOpenRegisterSession.set(null);
    }
  }
}
